import { CountryWithMCC, useGetCountryDataQuery } from '@/src/api/rtkQuery/countryApi'
import { Customer } from '@/src/types/itinerary'
import { ValidationState } from '@/src/utils/itinerary/travelerInputValidationRules'
import { Box, InputLabel, Typography } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import React, { memo, useMemo } from 'react'
import ItineraryAutoComplete from '../itinerary/auto-complete'
import ItineraryTextField from '../itinerary/itinearyTextField'

export interface ContactDetailsFormProps {
    email: string
    mobile: string
    mobileCountryCode: string
    validationState?: ValidationState
    onChange: (key: keyof Customer, value: string) => void
    onFieldBlur?: (fieldKey: string, inputValue?: any) => void
}

const ContactDetailsForm: React.FC<ContactDetailsFormProps> = ({
    email,
    mobile,
    mobileCountryCode,
    validationState,
    onChange,
    onFieldBlur
}) => {
    const t = useTranslations('itineraryPage.contactDetails')
    const commonTranslate = useTranslations('common')
    const locale = useLocale()
    const { data: countryData, isLoading: isCountryDataLoading } = useGetCountryDataQuery()

    // Helper function to get field validation state
    const getFieldValidation = (fieldName: string) => {
        return validationState?.[fieldName] || { isError: false, errorMessage: '', touched: false }
    }

    // Find selected country code option
    const selectedCountryCode = useMemo(
        () => countryData?.find(option => option.mobileCountryCode == mobileCountryCode) || null,
        [countryData, mobileCountryCode]
    )

    return (
        <>
            <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#111827', mb: 1 }}>
                {t('contactDetails')}
            </Typography>
            <Typography sx={{ color: 'grey.700', fontSize: 13, mb: 2 }}>{t('ticketSendMessage')}</Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    // gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: 2,
                    mb: 0
                }}
            >
                {/* Email */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        flex: 1,
                        minWidth: 250
                    }}
                >
                    <InputLabel shrink htmlFor="contact-email" sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}>
                        {t('emailAddress')}
                    </InputLabel>
                    <ItineraryTextField
                        type="email"
                        id="contact-email"
                        placeholder={t('emailPlaceholder')}
                        value={email}
                        onChange={e => onChange('email', e.target.value)}
                        onBlur={e => onFieldBlur?.('email', e.target.value)}
                        error={getFieldValidation('email').isError && getFieldValidation('email').touched}
                        helperText={getFieldValidation('email').touched && getFieldValidation('email').errorMessage}
                    />
                </Box>
                {/* Country Code + Mobile Number (spanning 2 columns) */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flex: 2 }}>
                    <Box flex={1}>
                        <ItineraryAutoComplete
                            disableClearable
                            loading={isCountryDataLoading}
                            label={t('mobileNumber')}
                            options={countryData || []}
                            value={selectedCountryCode}
                            onChange={value => {
                                onChange('mobileCountryCode', value?.mobileCountryCode || '')
                            }}
                            getOptionLabel={option =>
                                `+ ${option.mobileCountryCode} (${option[locale as keyof CountryWithMCC]})`
                            }
                            renderValueForMobileScreen={value => `+ ${value?.mobileCountryCode}`}
                            renderOption={(props: any, option) => (
                                <Typography {...props} key={props.key} sx={{ fontSize: 13 }}>
                                    {`+ ${option.mobileCountryCode} (${option[locale as keyof CountryWithMCC]})`}
                                </Typography>
                            )}
                            onBlur={() => onFieldBlur?.('mobileCountryCode')}
                            error={
                                getFieldValidation('mobileCountryCode').isError &&
                                getFieldValidation('mobileCountryCode').touched
                            }
                            errorMessage={
                                (getFieldValidation('mobileCountryCode').touched &&
                                    getFieldValidation('mobileCountryCode').errorMessage) ||
                                undefined
                            }
                            placeholder={t('select')}
                            groupBy={e => commonTranslate(e.category)}
                        />
                    </Box>
                    <Box sx={{ flex: 2 }}>
                        <InputLabel shrink htmlFor="mobile-number" sx={{ opacity: 0 }} required>
                            {t('mobileNumber')}
                        </InputLabel>
                        <ItineraryTextField
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*'
                            }}
                            id="mobile-number"
                            placeholder={t('mobilePlaceholder')}
                            value={mobile}
                            onChange={e => onChange('mobile', e.target.value)}
                            onBlur={e => onFieldBlur?.('mobile', e.target.value)}
                            error={getFieldValidation('mobile').isError && getFieldValidation('mobile').touched}
                            helperText={
                                getFieldValidation('mobile').touched && getFieldValidation('mobile').errorMessage
                            }
                            logerror="true"
                        />
                    </Box>
                </Box>
            </Box>
        </>
    )
}

const areEqual = (prevProps: ContactDetailsFormProps, nextProps: ContactDetailsFormProps) => {
    if (
        prevProps.email !== nextProps.email ||
        prevProps.mobile !== nextProps.mobile ||
        prevProps.mobileCountryCode !== nextProps.mobileCountryCode
    ) {
        return false
    }

    const keys = ['email', 'mobile', 'mobileCountryCode']
    const extract = (state?: ValidationState) =>
        keys
            .map(k => {
                const v = state?.[k]
                return v ? `${k}:${v.isError ? 1 : 0}:${v.touched ? 1 : 0}:${v.errorMessage || ''}` : `${k}:0:0:`
            })
            .join('|')

    if (extract(prevProps.validationState) !== extract(nextProps.validationState)) return false

    return true
}

export default memo(ContactDetailsForm, areEqual)
